(function () {
  'use strict';
  // Sortie anticipee : ce fichier est emis par 4 surfaces du theme, dont
  // alert-bikes-modal qui est rendu GLOBALEMENT. Sans elle, l'IIFE ci-dessous
  // empeche bien le SyntaxError mais laisse le corps se re-executer entierement :
  // les 3 document.addEventListener de fin de fichier seraient poses DEUX fois
  // (double init, AbortController et ResizeObserver ecrases). Le garde autour du
  // define, plus bas, ne protege que le define.
  if (customElements.get('product-carousel')) return;
  // Garde de double chargement : ce script est émis par plusieurs surfaces du thème
  // (alert-bikes-modal est rendu globalement sur toutes les pages, featured-collection
  // sur la homepage…). Sans cette IIFE, une seconde évaluation du même <script src>
  // lève `SyntaxError: Identifier 'ProductCarousel' has already been declared` avant
  // même d'atteindre le garde customElements.get plus bas — la classe est déclarée
  // au scope global. Le premier chargement, lui, SURVIT (l'erreur est levée en
  // GlobalDeclarationInstantiation du SECOND script) : seul ce second fichier meurt
  // entièrement. Le garde plus bas (`if (!customElements.get(...))`) ne protège
  // QUE le define : c'est la sortie anticipée ci-dessus qui protège le corps entier.

class ProductCarousel extends HTMLElement {
  constructor() {
    super();
    this.flickity = null;
    this.isInitialized = false;
    this.resizeTimeout = null;
    // Handler lié UNE fois : `bind()` crée une nouvelle fonction à chaque appel, donc
    // `removeEventListener(..., this.handleResize.bind(this))` ne retirait jamais rien.
    // Sans conséquence tant qu'aucun carrousel n'était détaché ; le `replaceChild` du
    // bloc « vus récemment » en détache un à chaque chargement.
    this.boundHandleResize = this.handleResize.bind(this);
  }

  connectedCallback() {
    this.init();
    window.addEventListener('resize', this.boundHandleResize);
  }

  disconnectedCallback() {
    this.destroy();
    window.removeEventListener('resize', this.boundHandleResize);
  }

  init() {
    if (this.isInitialized) return;

    this.waitForFlickity().then(() => {
      this.setupCarousel();
      this.isInitialized = true;
    });
  }

  async waitForFlickity() {
    if (window.Flickity) return;

    return new Promise((resolve) => {
      const checkFlickity = () => {
        if (window.Flickity) {
          resolve();
        } else {
          setTimeout(checkFlickity, 100);
        }
      };
      checkFlickity();
    });
  }

  setupCarousel() {
    const track = this.querySelector('.product-carousel__track');
    if (!track) return;

    // Mode statique : pas assez de produits pour remplir une vue.
    this.classList.toggle('is-static', this.isStatic());

    // Hauteur de la tuile CTA alignée sur les cartes AVANT l'init : Flickity
    // mesure les cellules à ce moment-là et en déduit la hauteur du viewport.
    this.syncCtaTileHeight();

    const options = this.getFlickityOptions();

    try {
      this.flickity = new Flickity(track, options);
      this.setupEventListeners();
      this.updateNavigationState();
      this.watchContentResize();
    } catch (error) {
      console.error("Erreur lors de l'initialisation de Flickity:", error);
    }
  }

  // adaptiveHeight verrouille la hauteur du viewport à l'init. Or les cartes
  // grandissent après coup (images lazyload, ligne de financement injectée par
  // greenleaze-rental-options.js…), ce qui provoquait des cartes rognées.
  // On re-mesure donc quand le contenu change.
  watchContentResize() {
    if (!this.flickity) return;

    const relayout = () => {
      this.syncCtaTileHeight();
      if (this.flickity) this.flickity.resize();
    };
    // Plusieurs images peuvent se charger dans la même frame : on regroupe.
    const scheduleRelayout = () => {
      if (this.contentResizeTimeout) clearTimeout(this.contentResizeTimeout);
      this.contentResizeTimeout = setTimeout(relayout, 150);
    };

    // Images lazyload : re-mesure à leur chargement. On écoute TOUTES les images,
    // sans filtre sur `complete` et sans `once` : une image en lazyload n'a pas
    // encore de src (donc `complete` vaut déjà true) et son vrai chargement, bien
    // plus tard, est justement celui qui fait grandir les cartes des dernières
    // pages. Le signal permet de tout détacher au destroy().
    this.contentListeners = new AbortController();
    const listenerOptions = { signal: this.contentListeners.signal };
    this.querySelectorAll('img').forEach((img) => {
      img.addEventListener('load', scheduleRelayout, listenerOptions);
      img.addEventListener('error', scheduleRelayout, listenerOptions);
    });

    // Contenu injecté de façon asynchrone (financement, etc.).
    [300, 800, 1500].forEach((delay) => setTimeout(relayout, delay));

    // Filet de sécurité : observe les variations de hauteur des cartes.
    if ('ResizeObserver' in window) {
      this.contentResizeObserver = new ResizeObserver(scheduleRelayout);
      // La tuile CTA est volontairement exclue : sa hauteur est pilotée par
      // syncCtaTileHeight(), l'observer se déclencherait donc en boucle.
      this.querySelectorAll('.product-carousel__slide:not(.product-carousel__slide--cta)').forEach((slide) => {
        this.contentResizeObserver.observe(slide);
      });
    }
  }

  // Tuile CTA de fin de carousel : sa hauteur ne peut pas venir du CSS. Une
  // cellule Flickity est en position absolue, donc `height: 100%` se résoudrait
  // contre le viewport, dont la hauteur est justement déduite de celle des
  // cellules par adaptiveHeight — la tuile se figeait sur la hauteur d'une page
  // précédente. On lui passe donc une valeur en px, mesurée sur les cartes qui
  // l'accompagnent : la tuile étant la dernière cellule et le carousel ne
  // bouclant pas, la dernière page affiche les (groupCells - 1) derniers
  // produits, puis la tuile.
  syncCtaTileHeight() {
    // La variable est posée sur la cellule (et non sur le lien) pour être héritée
    // par le lien ET son contenu, tous deux en height: 100%.
    const cell = this.querySelector('.product-carousel__slide--cta');
    if (!cell) return;

    const slides = Array.from(this.querySelectorAll('.product-carousel__slide:not(.product-carousel__slide--cta)'));
    if (!slides.length) return;

    const neighbours = slides.slice(-Math.max(1, this.getGroupCells() - 1));
    const tallest = Math.max(...neighbours.map((slide) => slide.offsetHeight));
    if (tallest > 0) cell.style.setProperty('--cta-tile-height', `${tallest}px`);
  }

  getFlickityOptions() {
    const autoplaySpeed = (parseInt(this.dataset.autoplaySpeed) || 5) * 1000;
    const staticMode = this.isStatic();

    return {
      cellAlign: 'left',
      contain: true,
      groupCells: this.getGroupCells(),
      pageDots: !staticMode,
      prevNextButtons: false,
      autoPlay: !staticMode && this.dataset.autoplay && this.dataset.autoplay !== 'false' ? autoplaySpeed : false,
      pauseAutoPlayOnHover: true,
      adaptiveHeight: true,
      // En mode statique : ni drag ni boucle, sinon les cartes défilent dans le vide.
      draggable: !staticMode,
      wrapAround: !staticMode && this.dataset.wrapAround !== 'false',
      accessibility: true,
      resize: true,
    };
  }

  getGroupCells() {
    const productsPerSlide = parseInt(this.dataset.productsPerSlide) || 4;
    const isMobile = window.innerWidth < 768;

    if (isMobile) {
      return window.innerWidth < 500 ? 1 : 2;
    }

    return productsPerSlide;
  }

  getCellCount() {
    return this.querySelectorAll('.product-carousel__slide').length;
  }

  // Vrai si tous les produits tiennent dans une seule vue : on désactive alors
  // navigation, drag et loop (Flickity gère très mal ce cas avec wrapAround).
  isStatic() {
    return this.getCellCount() <= this.getGroupCells();
  }

  setupEventListeners() {
    if (!this.flickity) return;

    const prevButton = this.querySelector('.product-carousel__arrow--prev');
    const nextButton = this.querySelector('.product-carousel__arrow--next');

    if (prevButton) {
      prevButton.addEventListener('click', () => {
        this.flickity.previous();
      });
    }

    if (nextButton) {
      nextButton.addEventListener('click', () => {
        this.flickity.next();
      });
    }

    this.flickity.on('select', () => this.updateNavigationState());
  }

  updateNavigationState() {
    if (!this.flickity) return;
    const prevButton = this.querySelector('.product-carousel__arrow--prev');
    const nextButton = this.querySelector('.product-carousel__arrow--next');
    const wrap = this.flickity.options && this.flickity.options.wrapAround;
    const idx = this.flickity.selectedIndex || 0;
    const last = this.flickity.slides ? this.flickity.slides.length - 1 : 0;
    if (prevButton) prevButton.disabled = !wrap && idx <= 0;
    if (nextButton) nextButton.disabled = !wrap && idx >= last;
    // Expose la position (début / fin) pour, ex., un dégradé indiquant qu'il reste des slides.
    this.classList.toggle('is-at-start', !wrap && idx <= 0);
    this.classList.toggle('is-at-end', !wrap && idx >= last);
  }

  handleResize() {
    if (this.resizeTimeout) {
      clearTimeout(this.resizeTimeout);
    }

    this.resizeTimeout = setTimeout(() => {
      if (!this.flickity) return;

      // Le nombre de colonnes visibles change avec la largeur : l'état statique
      // peut basculer. Dans ce cas on reconstruit avec les bonnes options.
      const wasStatic = this.classList.contains('is-static');
      if (wasStatic !== this.isStatic()) {
        this.refresh();
        return;
      }

      this.flickity.options.groupCells = this.getGroupCells();
      this.syncCtaTileHeight();
      this.flickity.resize();
    }, 250);
  }

  destroy() {
    if (this.flickity) {
      this.flickity.destroy();
      this.flickity = null;
    }
    this.isInitialized = false;

    if (this.resizeTimeout) {
      clearTimeout(this.resizeTimeout);
    }

    if (this.contentListeners) {
      this.contentListeners.abort();
      this.contentListeners = null;
    }

    if (this.contentResizeObserver) {
      this.contentResizeObserver.disconnect();
      this.contentResizeObserver = null;
    }
    if (this.contentResizeTimeout) {
      clearTimeout(this.contentResizeTimeout);
    }
  }

  refresh() {
    this.destroy();
    setTimeout(() => {
      this.init();
    }, 100);
  }
}

if (!customElements.get('product-carousel')) {
  customElements.define('product-carousel', ProductCarousel);
}

document.addEventListener('DOMContentLoaded', () => {
  const carousels = document.querySelectorAll('product-carousel');
  carousels.forEach((carousel) => {
    if (!carousel.isInitialized) {
      carousel.init();
    }
  });
});

document.addEventListener('shopify:section:load', (event) => {
  const carousel = event.target.querySelector('product-carousel');
  if (carousel) {
    carousel.refresh();
  }
});

document.addEventListener('shopify:section:unload', (event) => {
  const carousel = event.target.querySelector('product-carousel');
  if (carousel) {
    carousel.destroy();
  }
});
})();
